import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';
import {
  DeliveryGroupApplyPayload,
  DeliveryGroupLine,
  DeliveryGroupSummary,
  InvoiceImportInfoReason,
  InvoiceImportLine,
  InvoiceImportReviewSummary,
  InvoiceImportReviewThresholds,
  InvoiceImportSummary,
  SuggestedSalePriceRule,
} from 'src/app/models/invoice-import.model';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import { InvoiceImportService } from 'src/app/services/invoice-import.service';
import {
  apiErrorCode,
  applyBlockedReasonKey,
  attentionReasonKey,
  attentionReasonParams,
  effectiveTotalUnits,
  formatDkk,
  formatKrPlain,
  formatPercent,
  formatSignedKrDelta,
  infoReasonKey,
  isLargeReviewLine,
  isToCheckReviewLine,
  isUnchangedPendingLine,
  isUnmatchedProductLine,
  lineDraftFromLine,
  lineTypeKey,
  matchStatusKey,
  mergeUpdatedLine,
  resolveLineInfoReasons,
  reviewStatusKey,
  shouldShowInvoiceGroupHeader,
  sortInvoiceImportReviewLines,
  stockSkippedReasonKey,
  translateInstant,
  visibleAttentionReasons,
} from 'src/app/services/invoice-import.util';

type LineReviewFilter = 'needs_review' | 'large' | 'toCheck' | 'unchanged' | 'autoApproved' | 'all';

interface LineDraft {
  acceptCostPrice: boolean;
  newSalePrice: number | null;
  costKeepReason: string;
}

@Component({
  selector: 'app-delivery-group-detail',
  templateUrl: './delivery-group-detail.component.html',
  styleUrls: ['./delivery-group-detail.component.scss'],
})
export class DeliveryGroupDetailComponent implements OnInit {
  @Input() storeId!: string;
  @Input() groupKey!: string;
  @Output() closed = new EventEmitter<boolean>();
  @Output() openImport = new EventEmitter<string>();

  group: DeliveryGroupSummary | null = null;
  imports: InvoiceImportSummary[] = [];
  lines: DeliveryGroupLine[] = [];
  canApply = false;
  applyBlockedReasons: string[] = [];
  reviewSummary: InvoiceImportReviewSummary | null = null;
  thresholds: InvoiceImportReviewThresholds | null = null;
  loadError = '';
  lineFilter: LineReviewFilter = 'needs_review';
  autoApprovedPanelOpen = false;

  lineDrafts: Record<string, LineDraft> = {};
  lineApproveErrors: Record<string, string> = {};
  productSearchQ = '';
  productSearchResults: any[] = [];
  productSearchLineId: string | null = null;

  applySummary: DeliveryGroupApplyPayload | null = null;

  readonly costKeepQuickPickKeys = ['CAMPAIGN', 'WRONG_INVOICE', 'OTHER'] as const;
  readonly shouldShowInvoiceGroupHeader = shouldShowInvoiceGroupHeader;

  constructor(
    private invoiceImportService: InvoiceImportService,
    private ecommerceService: EcommerceService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadDetail();
  }

  loadDetail(): void {
    this.loadError = '';
    this.spinner.show();
    this.invoiceImportService.getDeliveryGroupDetail(this.storeId, this.groupKey).subscribe({
      next: (res: any) => {
        const data = res.response?.data || res.data || {};
        this.group = data.group || null;
        this.imports = data.imports || [];
        this.lines = data.lines || [];
        this.canApply = !!data.canApply;
        this.applyBlockedReasons = data.applyBlockedReasons || [];
        this.reviewSummary = data.reviewSummary || null;
        this.thresholds = data.thresholds || null;
        this.syncDrafts();
        this.spinner.hide();
      },
      error: (err: any) => {
        this.spinner.hide();
        this.loadError = err.error?.response?.message || err.message;
      },
    });
  }

  syncDrafts(): void {
    const next: Record<string, LineDraft> = {};
    for (const line of this.lines) {
      next[line._id] = lineDraftFromLine(line);
    }
    this.lineDrafts = next;
  }

  back(refresh = false): void {
    this.closed.emit(refresh);
  }

  openMemberImport(importId: string): void {
    this.openImport.emit(importId);
  }

  setLineFilter(filter: LineReviewFilter): void {
    this.lineFilter = filter;
    this.autoApprovedPanelOpen = filter === 'autoApproved';
  }

  get autoApprovedLines(): DeliveryGroupLine[] {
    return sortInvoiceImportReviewLines(
      this.lines.filter((l) => l.lineType === 'product' && l.reviewStatus === 'auto_approved')
    ) as DeliveryGroupLine[];
  }

  get visibleLines(): DeliveryGroupLine[] {
    let pool = this.lines.filter((l) => l.lineType === 'product');
    switch (this.lineFilter) {
      case 'large':
        pool = pool.filter((l) => isLargeReviewLine(l));
        break;
      case 'toCheck':
        pool = pool.filter((l) => isToCheckReviewLine(l));
        break;
      case 'autoApproved':
        pool = pool.filter((l) => l.reviewStatus === 'auto_approved');
        break;
      case 'unchanged':
        pool = pool.filter((l) => isUnchangedPendingLine(l));
        break;
      case 'all':
        break;
      case 'needs_review':
      default:
        pool = pool.filter(
          (l) =>
            l.reviewStatus !== 'auto_approved' && (isLargeReviewLine(l) || isToCheckReviewLine(l))
        );
        break;
    }
    return sortInvoiceImportReviewLines(pool) as DeliveryGroupLine[];
  }

  get unchangedPendingCount(): number {
    return this.reviewSummary?.unchangedPending ?? 0;
  }

  get showUnchangedPendingHint(): boolean {
    return this.lineFilter === 'needs_review' && this.unchangedPendingCount > 0;
  }

  get applyLineCount(): number {
    return this.lines.filter(
      (l) =>
        l.lineType === 'product' &&
        (l.reviewStatus === 'approved' ||
          l.reviewStatus === 'auto_approved' ||
          l.reviewStatus === 'auto_marked')
    ).length;
  }

  get showNoLinesState(): boolean {
    return this.lines.length === 0;
  }

  get showEmptyFilterState(): boolean {
    return !this.showNoLinesState && this.visibleLines.length === 0 && !this.autoApprovedPanelOpen;
  }

  formatMoney = formatDkk;
  formatKrPlain = formatKrPlain;
  formatPercent = formatPercent;
  visibleReasons = visibleAttentionReasons;

  statusLabelKey(status: string): string {
    return `INVOICE_IMPORTS.STATUS.${status.toUpperCase()}`;
  }

  importStatusBadgeCount(status: string): number {
    return this.group?.statuses?.[status] ?? 0;
  }

  groupStatusKeys(): string[] {
    const statuses = this.group?.statuses;
    if (!statuses) {
      return [];
    }
    return Object.keys(statuses).filter((k) => (statuses[k] ?? 0) > 0);
  }

  attentionLabel(line: InvoiceImportLine, reason: string): string {
    const key = attentionReasonKey(reason as any);
    return translateInstant(this.translate, key, attentionReasonParams(reason as any, line));
  }

  lineInfoReasons(line: InvoiceImportLine): InvoiceImportInfoReason[] {
    return resolveLineInfoReasons(line);
  }

  infoReasonLabel(reason: InvoiceImportInfoReason): string {
    return translateInstant(this.translate, infoReasonKey(reason));
  }

  blockedReasonLabel(code: string): string {
    return translateInstant(this.translate, applyBlockedReasonKey(code));
  }

  stockSkippedLabel(code: string | undefined): string {
    if (!code) {
      return '';
    }
    return translateInstant(this.translate, stockSkippedReasonKey(code));
  }

  costSkippedLabel(code: string | undefined): string {
    if (!code) {
      return '';
    }
    return translateInstant(this.translate, `INVOICE_IMPORTS.COST_SKIPPED.${code.toUpperCase()}`);
  }

  reviewStatusLabel(status: string): string {
    return translateInstant(this.translate, reviewStatusKey(status));
  }

  severityLabel(severity: string | undefined): string {
    if (!severity || severity === 'none' || severity === 'small') {
      return '';
    }
    return translateInstant(this.translate, `INVOICE_IMPORTS.SEVERITY.${severity.toUpperCase()}`);
  }

  lineTypeLabel(lineType: string): string {
    return translateInstant(this.translate, lineTypeKey(lineType));
  }

  matchStatusLabel(matchStatus: string | undefined): string {
    if (!matchStatus) {
      return '';
    }
    return translateInstant(this.translate, matchStatusKey(matchStatus));
  }

  lineImportId(line: DeliveryGroupLine): string {
    return line.importId;
  }

  quantityDisplay(line: DeliveryGroupLine): string {
    const stock = line.stockUnits;
    const counted = line.countedUnits;
    if (counted != null && !Number.isNaN(counted)) {
      return translateInstant(this.translate, 'INVOICE_IMPORTS.DELIVERY_GROUPS.QTY_STOCK_COUNTED', {
        stock,
        counted,
      });
    }
    return translateInstant(this.translate, 'INVOICE_IMPORTS.DELIVERY_GROUPS.QTY_STOCK', {
      stock: stock ?? effectiveTotalUnits(line),
    });
  }

  isApprovableLine(line: DeliveryGroupLine): boolean {
    return (
      line.lineType === 'product' &&
      line.reviewStatus !== 'imported' &&
      line.reviewStatus !== 'applying' &&
      line.reviewStatus !== 'auto_approved'
    );
  }

  showUseRecommendedPrice(line: DeliveryGroupLine): boolean {
    const rec = line.recommendedRetailInclVat;
    if (rec == null || Number.isNaN(rec)) {
      return false;
    }
    if (line.suggestedSalePriceRule === 'recommended_retail') {
      return false;
    }
    const current = line.productSnapshot?.price;
    if (current == null || Number.isNaN(current)) {
      return true;
    }
    return Math.abs(rec - current) > 0.005;
  }

  showRecommendedOnly(line: DeliveryGroupLine): boolean {
    return line.productSnapshot == null && line.recommendedRetailInclVat != null;
  }

  recommendedOnlyLabel(line: DeliveryGroupLine): string {
    return translateInstant(this.translate, 'INVOICE_IMPORTS.DETAIL.RECOMMENDED_ONLY', {
      rec: formatKrPlain(line.recommendedRetailInclVat!),
    });
  }

  newCostOnlyLabel(line: DeliveryGroupLine): string {
    const cost = line.newCostExclVat ?? line.netUnitCostExclVat;
    if (cost == null) {
      return '';
    }
    return translateInstant(this.translate, 'INVOICE_IMPORTS.DETAIL.NEW_COST_ONLY', {
      cost: formatKrPlain(cost),
    });
  }

  costChangeLabel(line: DeliveryGroupLine): string {
    const oldCost = line.productSnapshot?.costPrice;
    const newCost = line.proposedCostPrice;
    if (oldCost == null || newCost == null) {
      return '';
    }
    return translateInstant(this.translate, 'INVOICE_IMPORTS.DETAIL.COST_CHANGE', {
      old: formatKrPlain(oldCost),
      new: formatKrPlain(newCost),
      deltaKr: formatSignedKrDelta(line.costDelta),
      deltaPct: formatPercent(line.costDeltaPercent),
    });
  }

  retailGapLabel(line: DeliveryGroupLine): string {
    if (line.recommendedRetailInclVat == null || line.retailGap == null) {
      return '';
    }
    const key =
      line.retailGap < 0
        ? 'INVOICE_IMPORTS.DETAIL.RETAIL_GAP_BELOW'
        : line.retailGap > 0
          ? 'INVOICE_IMPORTS.DETAIL.RETAIL_GAP_ABOVE'
          : 'INVOICE_IMPORTS.DETAIL.RETAIL_GAP_EQUAL';
    return translateInstant(this.translate, key, {
      rec: formatKrPlain(line.recommendedRetailInclVat),
      deltaKr: formatSignedKrDelta(line.retailGap),
      deltaPct: formatPercent(line.retailGapPercent),
    });
  }

  marginAtCurrentLabel(line: DeliveryGroupLine): string {
    const min = this.thresholds?.minimumMarginPercent ?? 15;
    const low = line.marginPercentAtCurrentPrice != null && line.marginPercentAtCurrentPrice < min;
    return translateInstant(this.translate, 'INVOICE_IMPORTS.DETAIL.MARGIN_AT_CURRENT', {
      value: formatPercent(line.marginPercentAtCurrentPrice, 1),
      low: low ? this.translate.instant('INVOICE_IMPORTS.DETAIL.MARGIN_LOW', { min }) : '',
    });
  }

  marginTransitionLabel(line: DeliveryGroupLine): string {
    if (line.marginPercentAtCurrentPriceOldCost == null || line.marginPercentAtCurrentPrice == null) {
      return '';
    }
    return translateInstant(this.translate, 'INVOICE_IMPORTS.DETAIL.MARGIN_TRANSITION', {
      old: formatPercent(line.marginPercentAtCurrentPriceOldCost, 1),
      new: formatPercent(line.marginPercentAtCurrentPrice, 1),
    });
  }

  marginAtRecommendedLabel(line: DeliveryGroupLine): string {
    return translateInstant(this.translate, 'INVOICE_IMPORTS.DETAIL.MARGIN_AT_RECOMMENDED', {
      value: formatPercent(line.marginPercentAtRecommendedPrice, 1),
    });
  }

  suggestedPriceChipLabel(line: DeliveryGroupLine): string {
    const rule = line.suggestedSalePriceRule as SuggestedSalePriceRule | null | undefined;
    const price = line.suggestedSalePrice;
    if (!rule || price == null) {
      return '';
    }
    const min = this.thresholds?.minimumMarginPercent ?? 15;
    return translateInstant(this.translate, `INVOICE_IMPORTS.DETAIL.SUGGESTED_PRICE.${rule.toUpperCase()}`, {
      price: formatKrPlain(price),
      min,
    });
  }

  autoApprovalTooltip(line: DeliveryGroupLine): string {
    const aa = line.autoApproval;
    if (!aa) {
      return '';
    }
    return translateInstant(this.translate, 'INVOICE_IMPORTS.DETAIL.AUTO_APPROVAL_TOOLTIP', {
      rule: aa.rule,
      small: aa.thresholds.smallPercent,
      min: aa.thresholds.minimumMarginPercent,
      approvedAt: aa.approvedAt,
    });
  }

  toggleAutoApprovedPanel(): void {
    this.autoApprovedPanelOpen = !this.autoApprovedPanelOpen;
    if (this.autoApprovedPanelOpen) {
      this.lineFilter = 'autoApproved';
    }
  }

  lineProductName(line: DeliveryGroupLine): string {
    return line.productSnapshot?.name || line.name || '—';
  }

  lineImageUrl(line: DeliveryGroupLine): string | null {
    return line.productSnapshot?.imageUrl || null;
  }

  isMarginBelowMinimum(line: DeliveryGroupLine): boolean {
    const min = this.thresholds?.minimumMarginPercent;
    if (min == null || line.marginPercentAtCurrentPrice == null) {
      return false;
    }
    return line.marginPercentAtCurrentPrice < min;
  }

  trackByLineId(_index: number, line: DeliveryGroupLine): string {
    return line._id;
  }

  patchLineField(line: DeliveryGroupLine): void {
    const draft = this.lineDrafts[line._id];
    if (!draft || line.reviewStatus === 'imported' || line.reviewStatus === 'applying') {
      return;
    }
    const body: Record<string, unknown> = {
      acceptCostPrice: draft.acceptCostPrice,
      newSalePrice: draft.newSalePrice,
    };
    this.invoiceImportService.patchLine(this.storeId, this.lineImportId(line), line._id, body).subscribe({
      next: (res: any) => this.applyLineResponse(res, this.lineImportId(line)),
      error: (err: any) => {
        this.toastr.error(err.error?.response?.message || err.message);
      },
    });
  }

  onAcceptCostToggle(line: DeliveryGroupLine): void {
    const draft = this.lineDrafts[line._id];
    if (draft?.acceptCostPrice) {
      draft.costKeepReason = '';
    }
    this.patchLineField(line);
  }

  applyCostKeepQuickPick(line: DeliveryGroupLine, pickKey: string): void {
    const draft = this.lineDrafts[line._id];
    if (!draft) {
      return;
    }
    draft.costKeepReason = this.translate.instant(`INVOICE_IMPORTS.DETAIL.COST_KEEP_PICK.${pickKey}`);
  }

  costKeepQuickPickLabel(pickKey: string): string {
    return this.translate.instant(`INVOICE_IMPORTS.DETAIL.COST_KEEP_PICK.${pickKey}`);
  }

  useSuggestedPrice(line: DeliveryGroupLine): void {
    const draft = this.lineDrafts[line._id];
    if (!draft || line.suggestedSalePrice == null) {
      return;
    }
    draft.newSalePrice = line.suggestedSalePrice;
    this.afterDraftSalePriceSet(line);
  }

  useRecommendedPrice(line: DeliveryGroupLine): void {
    const draft = this.lineDrafts[line._id];
    if (!draft || line.recommendedRetailInclVat == null) {
      return;
    }
    draft.newSalePrice = line.recommendedRetailInclVat;
    this.afterDraftSalePriceSet(line);
  }

  private afterDraftSalePriceSet(line: DeliveryGroupLine): void {
    if (isUnmatchedProductLine(line)) {
      this.patchLineField(line);
      this.startProductSearch(line);
      this.toastr.info(this.translate.instant('INVOICE_IMPORTS.DETAIL.LINK_TO_APPROVE'));
      return;
    }
    this.approveLine(line);
  }

  undoAutoApprove(line: DeliveryGroupLine): void {
    const importId = this.lineImportId(line);
    this.spinner.show();
    this.invoiceImportService.undoAutoApproveLine(this.storeId, importId, line._id).subscribe({
      next: (res: any) => {
        this.spinner.hide();
        this.applyLineResponse(res, importId);
        this.toastr.success(this.translate.instant('INVOICE_IMPORTS.DETAIL.UNDO_AUTO_APPROVE_DONE'));
      },
      error: (err: any) => {
        this.spinner.hide();
        Swal.fire('Error', err.error?.response?.message || err.message, 'error');
      },
    });
  }

  approveLine(line: DeliveryGroupLine): void {
    const draft = this.lineDrafts[line._id];
    const importId = this.lineImportId(line);
    this.lineApproveErrors[line._id] = '';
    const body: Record<string, unknown> = {
      acceptCostPrice: draft?.acceptCostPrice !== false,
      newSalePrice: draft?.newSalePrice,
    };
    if (draft && !draft.acceptCostPrice) {
      body['costKeepReason'] = (draft.costKeepReason || '').trim();
    }
    this.spinner.show();
    this.invoiceImportService.approveLine(this.storeId, importId, line._id, body).subscribe({
      next: (res: any) => {
        this.spinner.hide();
        this.applyLineResponse(res, importId);
        this.toastr.success(this.translate.instant('INVOICE_IMPORTS.DETAIL.LINE_APPROVED'));
      },
      error: (err: any) => {
        this.spinner.hide();
        const code = apiErrorCode(err);
        if (code === 'COST_KEEP_REASON_REQUIRED') {
          this.lineApproveErrors[line._id] = this.translate.instant(
            'INVOICE_IMPORTS.DETAIL.COST_KEEP_REASON_REQUIRED'
          );
          return;
        }
        Swal.fire('Error', err.error?.response?.message || err.message, 'error');
      },
    });
  }

  rejectLine(line: DeliveryGroupLine): void {
    const importId = this.lineImportId(line);
    Swal.fire({
      title: this.translate.instant('INVOICE_IMPORTS.DETAIL.REJECT_TITLE'),
      text: this.translate.instant('INVOICE_IMPORTS.DETAIL.REJECT_TOOLTIP'),
      input: 'textarea',
      showCancelButton: true,
      confirmButtonText: this.translate.instant('INVOICE_IMPORTS.DETAIL.REJECT_CONFIRM'),
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }
      this.spinner.show();
      this.invoiceImportService
        .rejectLine(this.storeId, importId, line._id, result.value || undefined)
        .subscribe({
          next: (res: any) => {
            this.spinner.hide();
            this.applyLineResponse(res, importId);
          },
          error: (err: any) => {
            this.spinner.hide();
            Swal.fire('Error', err.error?.response?.message || err.message, 'error');
          },
        });
    });
  }

  bulkApproveUnchanged(): void {
    this.clearApplySummary();
    this.spinner.show();
    this.invoiceImportService.bulkApproveDeliveryGroupUnchanged(this.storeId, this.groupKey).subscribe({
      next: (res: any) => {
        this.spinner.hide();
        const data = res.response?.data || res.data || {};
        this.toastr.success(
          this.translate.instant('INVOICE_IMPORTS.DELIVERY_GROUPS.BULK_APPROVED', { count: data.approved || 0 })
        );
        this.loadDetail();
      },
      error: (err: any) => {
        this.spinner.hide();
        Swal.fire('Error', err.error?.response?.message || err.message, 'error');
      },
    });
  }

  confirmApply(): void {
    const count = this.applyLineCount;
    Swal.fire({
      title: this.translate.instant('INVOICE_IMPORTS.DELIVERY_GROUPS.APPLY_TITLE'),
      text: this.translate.instant('INVOICE_IMPORTS.DELIVERY_GROUPS.APPLY_TEXT', { count }),
      icon: 'question',
      showCancelButton: true,
    }).then((r) => {
      if (r.isConfirmed) {
        this.runApply();
      }
    });
  }

  runApply(): void {
    this.clearApplySummary();
    this.spinner.show();
    this.invoiceImportService.applyDeliveryGroup(this.storeId, this.groupKey).subscribe({
      next: (res: any) => {
        this.spinner.hide();
        this.applySummary = res.response?.data || res.data;
        this.toastr.success(this.translate.instant('INVOICE_IMPORTS.DELIVERY_GROUPS.APPLY_DONE'));
        this.loadDetail();
      },
      error: (err: any) => {
        this.spinner.hide();
        const code = apiErrorCode(err);
        const msg = err.error?.response?.message || err.message;
        Swal.fire('Error', code ? `${msg} (${code})` : msg, 'error');
      },
    });
  }

  clearApplySummary(): void {
    this.applySummary = null;
  }

  startProductSearch(line: DeliveryGroupLine): void {
    this.productSearchLineId = line._id;
    this.productSearchQ = line.ean || line.name || '';
    this.searchProducts();
  }

  searchProducts(): void {
    if (!this.productSearchQ || this.productSearchQ.length < 2) {
      this.productSearchResults = [];
      return;
    }
    this.ecommerceService
      .getStoreProducts(this.storeId, { page: 1, limit: 10, search: this.productSearchQ, adminView: true })
      .subscribe({
        next: (res: any) => {
          const data = res.response?.data || res.data || {};
          this.productSearchResults = data.products || [];
        },
        error: () => {
          this.productSearchResults = [];
        },
      });
  }

  linkProduct(line: DeliveryGroupLine, productId: string): void {
    const importId = this.lineImportId(line);
    const draft = this.lineDrafts[line._id];
    const body: Record<string, unknown> = { productId };
    if (draft?.newSalePrice != null) {
      body['newSalePrice'] = draft.newSalePrice;
    }
    this.spinner.show();
    this.invoiceImportService.patchLine(this.storeId, importId, line._id, body).subscribe({
      next: (res: any) => {
        this.spinner.hide();
        this.productSearchLineId = null;
        this.applyLineResponse(res, importId);
      },
      error: (err: any) => {
        this.spinner.hide();
        Swal.fire('Error', err.error?.response?.message || err.message, 'error');
      },
    });
  }

  applyResultErrorLabel(item: { error?: { code?: string; message?: string } }): string {
    const msg = item.error?.message || '';
    const code = item.error?.code;
    return code ? `${msg} (${code})` : msg;
  }

  private applyLineResponse(res: any, importId: string): void {
    const data = res.response?.data || res.data || {};
    if (data.line) {
      this.lines = mergeUpdatedLine(this.lines, data.line) as DeliveryGroupLine[];
    }
    if (data.lineCounts) {
      this.imports = this.imports.map((imp) =>
        imp._id === importId ? { ...imp, lineCounts: data.lineCounts } : imp
      );
      if (this.group) {
        this.group = { ...this.group, lineCounts: data.lineCounts };
      }
    }
    if (data.reviewSummary) {
      this.reviewSummary = data.reviewSummary;
      if (this.group) {
        this.group = { ...this.group, reviewSummary: data.reviewSummary };
      }
    }
    if (data.line) {
      this.lineDrafts[data.line._id] = lineDraftFromLine(data.line);
    }
  }
}
